// src/pages/Login.tsx
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithCustomToken,
} from 'firebase/auth'
import { auth, emailActionSettings } from '../firebase'
import { api } from '../api'

const LS_EMAIL_KEY = 'eucamd.magiclink.email'
const LS_BACKEND_TOKEN = 'eucamd.backend.jwt'
const LS_POST_LOGIN_REDIRECT = 'postLoginRedirect'
const LS_EXPERIENCE_KEY = 'euca.lastExperience'

const detectInAppWebview = () => {
  if (typeof navigator === 'undefined') return false
  const uaRaw = navigator.userAgent || ''
  const ua = uaRaw.toLowerCase()
  const hints = [
    'fban',
    'fbav',
    'fb_iab',
    'fb4a',
    'fbios',
    'fbandroid',
    'instagram',
    'line/',
    'micromessenger',
    'wv;',
    'okhttp',
    'fxios',
    'kakaotalk',
  ]
  return hints.some((h) => ua.includes(h))
}

const isAndroid = () => typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent || '')
const isWeChat = () => typeof navigator !== 'undefined' && /micromessenger/i.test(navigator.userAgent || '')

const decodeEucaPayload = (token: string) => {
  if (!token || !token.startsWith('EUCA ')) return null
  const raw = token.slice('EUCA '.length)
  const [payloadB64] = raw.split('.')
  if (!payloadB64) return null
  try {
    let padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/')
    const padLen = padded.length % 4
    if (padLen) padded += '='.repeat(4 - padLen)
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}

const tokenIndicatesProvider = (token: string) => {
  const payload = decodeEucaPayload(token)
  if (!payload) return false
  const roles = new Set<string>()
  const addRole = (value: unknown) => {
    const role = String(value || '').trim().toLowerCase()
    if (role) roles.add(role)
  }
  addRole((payload as any).role)
  if (Array.isArray((payload as any).roles)) {
    ;(payload as any).roles.forEach(addRole)
  }
  return roles.has('specialist') || roles.has('practice') || roles.has('provider')
}

const forceProviderAppointmentsView = (target: string) => {
  if (!target.startsWith('/appointments')) return target
  try {
    const url = new URL(target, window.location.origin)
    const currentView = (url.searchParams.get('view') || '').trim().toLowerCase()
    if (currentView === 'provider' || currentView === 'specialist') {
      return `${url.pathname}${url.search}${url.hash}`
    }
    url.searchParams.set('view', 'provider')
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return target
  }
}

const resolveNextTarget = ({ forceProvider = false }: { forceProvider?: boolean } = {}) => {
  if (typeof window === 'undefined') return '/dashboard'
  const params = new URLSearchParams(window.location.search)
  const next = (params.get('next') || '').trim()
  const exp = (params.get('exp') || '').trim()
  const shouldForceProvider = forceProvider || exp === 'provider'
  let target = '/dashboard'
  if (next && next.startsWith('/')) {
    target = next
  } else if (exp === 'client') {
    target = '/clientdashboard'
  }
  if (!shouldForceProvider) return target
  if (target.startsWith('/clients')) return '/dashboard'
  return forceProviderAppointmentsView(target)
}

export default function Login() {
  const nav = useNavigate()
  const loc = useLocation()

  const [email, setEmail] = useState('')
  const [inAppWebview, setInAppWebview] = useState(() => detectInAppWebview())
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const showFacebook = false

  // 0) capture backend token if present (?token=...)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search)
    const tok = sp.get('token')
    const exp = (sp.get('exp') || '').trim().toLowerCase()
    if (tok) {
      window.localStorage.setItem(LS_BACKEND_TOKEN, tok)
      if (tokenIndicatesProvider(tok) || exp === 'provider') {
        window.localStorage.setItem(LS_EXPERIENCE_KEY, 'provider')
      }
      const url = new URL(window.location.href)
      url.searchParams.delete('token')
      window.history.replaceState({}, '', url.toString())
    } else if (exp === 'provider') {
      window.localStorage.setItem(LS_EXPERIENCE_KEY, 'provider')
    }
  }, [])

  // 1) if we already have a backend token → verify it once and go to target
  useEffect(() => {
    ;(async () => {
      const backendTok = window.localStorage.getItem(LS_BACKEND_TOKEN)
      if (!backendTok) return
      const providerFlow =
        tokenIndicatesProvider(backendTok) ||
        (new URLSearchParams(window.location.search).get('exp') || '').trim().toLowerCase() === 'provider'
      const target = resolveNextTarget({ forceProvider: providerFlow })
      try {
        if (backendTok.startsWith('EUCA ')) {
          const res = await api.exchangeAuth().catch(() => null)
          const customToken = res?.customToken
          if (!customToken) throw new Error('Missing custom token')
          await signInWithCustomToken(auth, customToken)
          if (providerFlow) {
            window.localStorage.setItem(LS_EXPERIENCE_KEY, 'provider')
          }
          nav(target, { replace: true })
          return
        }
        // we still hit the API so it can create the user if needed
        await api.me()
        if (providerFlow) {
          window.localStorage.setItem(LS_EXPERIENCE_KEY, 'provider')
        }
        nav(target, { replace: true })
      } catch {
        // token might be invalid → clear it so we don’t loop
        window.localStorage.removeItem(LS_BACKEND_TOKEN)
      }
    })()
  }, [nav])

  // 2) handle Firebase email-link sign in
  useEffect(() => {
    ;(async () => {
      try {
        if (!isSignInWithEmailLink(auth, window.location.href)) return
        setBusy(true)
        let saved = window.localStorage.getItem(LS_EMAIL_KEY) || ''
        if (!saved) {
          const promptEmail = window.prompt('Please confirm your email for sign-in') || ''
          saved = promptEmail.trim()
        }
        if (!saved) {
          setError('Email is required to complete sign-in.')
          setBusy(false)
          return
        }
        await signInWithEmailLink(auth, saved, window.location.href)
        window.localStorage.removeItem(LS_EMAIL_KEY)
        setInfo('Signed in successfully.')
        const target = window.localStorage.getItem(LS_POST_LOGIN_REDIRECT) || '/dashboard'
        window.localStorage.removeItem(LS_POST_LOGIN_REDIRECT)
        nav(target, { replace: true })
      } catch (e: any) {
        setError(e?.message || 'Could not complete email link sign-in.')
      } finally {
        setBusy(false)
      }
    })()
  }, [loc.key, nav])

  // backend Google OAuth (unchanged)
  const onGoogleBackend = () => {
    const apiBase = import.meta.env.VITE_API_BASE ?? 'https://eucamd-match-api.example'
    const qs = window.location.search || ''
    window.location.href = `${apiBase}/auth/google/start${qs}`
  }

  const onFacebookBackend = () => {
    const apiBase = import.meta.env.VITE_API_BASE ?? 'https://eucamd-match-api.example'
    const qs = window.location.search || ''
    window.location.href = `${apiBase}/auth/facebook/start${qs}`
  }

  const onMagic = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setInfo(null)
    const target = email.trim()
    if (!target) return setError('Enter your email first.')
    setBusy(true)
    try {
      await sendSignInLinkToEmail(auth, target, emailActionSettings)
      window.localStorage.setItem(LS_EMAIL_KEY, target)
      setInfo('We’ve emailed you a sign-in link. Open it on this device to continue. Check your junk/spam if you don’t see it.')
    } catch (err: any) {
      setError(err?.message || 'Could not send magic link')
    } finally {
      setBusy(false)
    }
  }

  // Persist intended redirect (next/exp/token) for post-OAuth/callback flows
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const hasNext = Boolean((params.get('next') || '').trim())
    const hasExp = Boolean((params.get('exp') || '').trim())
    const hasToken = Boolean((params.get('token') || '').trim())
    if (hasNext || hasExp || hasToken) {
      const target = resolveNextTarget()
      if (target) {
        window.localStorage.setItem(LS_POST_LOGIN_REDIRECT, target)
      }
    }
  }, [])

  // Detect in-app browsers that block Google OAuth (e.g., FB Messenger, WeChat)
  useEffect(() => {
    setInAppWebview(detectInAppWebview())
  }, [])

  const openInBrowserHref = useMemo(() => {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    const next = params.get('next')
    if (next && next.startsWith('/')) {
      url.searchParams.set('next', next)
    }
    const raw = url.toString()
    if (isAndroid()) {
      return `intent://${raw.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end;`
    }
    return raw
  }, [loc.key])

  return (
    <div className="container">
      <div style={{ maxWidth: 440, margin: '0 auto', paddingTop: 32 }}>
        <div className="card" style={{ width: '100%', borderRadius: 'var(--radius-2xl)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 20,
              justifyContent: 'flex-start',
            }}
          >
            <img
              src="/logo.png"
              alt="EUCA Match"
              width={60}
              height={60}
              style={{ borderRadius: 10 }}
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontWeight: 800, fontSize: 26, color: '#2E7D32', lineHeight: 1.05, letterSpacing: 0.1 }}>
                EUCA Match
              </span>
            </div>
          </div>
          <h2 style={{ marginTop: 0, textAlign: 'center', fontWeight: 600, marginBottom: 16 }}>
            Log in or sign up
          </h2>

          <div className="list" style={{ gap: 12 }}>
            {inAppWebview ? (
              <div className="banner warn" role="alert" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p style={{ margin: 0, lineHeight: 1.4 }}>
                  Google/Apple sign-in is blocked inside this in-app browser.
                </p>
                <a className="btn cta" href={openInBrowserHref} style={{ whiteSpace: 'nowrap' }}>
                  Open in browser
                </a>
                <p className="hint" style={{ margin: 0, color: '#475569' }}>
                  If it doesn’t open: tap the ⋮ (top right), then “Open in browser”.
                </p>
              </div>
            ) : (
              <>
                <button
                  className="btn"
                  type="button"
                  onClick={onGoogleBackend}
                  disabled={busy}
                  style={{ borderRadius: 'var(--radius-xl)', display: 'inline-flex', gap: 8 }}
                >
                  <img src="/gicon.png" alt="" width={22} height={22} style={{ display: 'inline-block' }} />
                  Continue with Google
                </button>
                <button
                  className="btn"
                  type="button"
                  onClick={() => {
                    const apiBase = import.meta.env.VITE_API_BASE ?? 'https://54uqjwvuke.execute-api.ap-southeast-2.amazonaws.com/prod'
                    const qs = window.location.search || ''
                    window.location.href = `${apiBase}/auth/apple/start${qs}`
                  }}
                  disabled={busy}
                  style={{ borderRadius: 'var(--radius-xl)', display: 'inline-flex', gap: 8, alignItems: 'center' }}
                >
                  <img src="/apple.png" alt="" width={17} style={{ display: 'inline-block', height: 'auto' }} />
                  Continue with Apple
                </button>
                {showFacebook && (
                  <button
                    className="btn"
                    type="button"
                    onClick={onFacebookBackend}
                    disabled={busy}
                    style={{ borderRadius: 'var(--radius-xl)', display: 'inline-flex', gap: 8 }}
                  >
                    <img src="/facebook.png" alt="" width={16} height={16} style={{ display: 'inline-block' }} />
                    Continue with Facebook
                  </button>
                )}
              </>
            )}

            <div
              className="helper"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                color: '#6b7280',
                fontSize: 14,
              }}
            >
              <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
              OR
              <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
            </div>

            <form onSubmit={onMagic} className="list" noValidate>
              <input
                className="input"
                type="email"
                placeholder="Email address"
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={{ borderRadius: 'var(--radius-xl)', textAlign: 'center' }}
              />
              <button
                className="btn btn-primary"
                type="submit"
                disabled={busy || !email}
                style={{ borderRadius: 'var(--radius-xl)' }}
              >
                {busy && <span className="btn-spinner" aria-hidden="true" />}
                Continue
              </button>
            </form>

            {info && <div className="helper" style={{ color: 'var(--muted)' }}>{info}</div>}
            {error && <div className="helper" style={{ color: '#b91c1c' }}>{error}</div>}
          </div>
        </div>
      </div>
    </div>
  )
}
